import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment';
import Calendar from './Calendar';
import apiClient from '../../Services/ApiClient';
import Form from '../Form/Form';
import FormControl from '../Form/FormControl';
import { useEffectAsync } from '../../helpers';
import { WORKPLACES } from '../../Modules/Workplace/Graphql/workplaces.graphql';
import styles from './AllWorkplacesCalendar.module.scss';
import { useAppContext } from '../../Services/AppContext';
import Select from '../Base/Select/Select';
import useCalendarEvent from '../../Modules/Calendar/Hooks/useCalendarEvent';
import useTranslation from '../../Hooks/useTranslation';
import useWorkplaceEvents from '../../Modules/Events/Hooks/useWorkplaceEvents';
import { getMomentDate } from '../../Utils/dates.util';
import CalendarAbsenceEventModal from '../../Modules/Absence/Components/CalendarAbsenceEvent/CalendarAbsenceEventModal';
import { EventApi, EventClickArg } from '@fullcalendar/react';

interface Workplace {
	id: string;
	value: string;
	title: string;
	label: string;
}

export default function AbsenceCalendar() {
	const { workplaceId } = useAppContext();
	const { t } = useTranslation();
	const { renderAbsenceEvent } = useCalendarEvent();

	const [isDetailOpened, setIsDetailOpened] = useState(false);
	const [selectedDate, setSelectedDate] = useState<Moment | undefined>();
	const [selectedEvent, setSelectedEvent] = useState<
		EventClickArg | undefined
	>();
	const [dateRange, setDateRange] = useState<
		{ start: Moment; end: Moment } | undefined
	>(undefined);
	const [events, setEvents] = useState<any[]>([]);
	const [loadingEvents, setLoadingEvents] = useState(false);
	const [resources, setResources] = useState<Workplace[]>([]);
	const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
	const [workplacesIds, setWorkplacesIds] = useState<string[]>([]);
	const [defaultValues, setDefaultValues] = useState({});
	const [initialized, setInitialized] = useState(false);

	const { getWorkersAbsenceEvents } = useWorkplaceEvents();

	useEffectAsync(async () => {
		await setInitialized(false);
		await setInitialized(true);
	}, [defaultValues]);

	useEffectAsync(async () => {
		await handleLoadResources();
	}, []);

	useEffect(() => {
		handleSetResources();
	}, [workplacesIds]);

	useEffect(() => {
		handleSetResources();
	}, [workplaces]);

	useEffectAsync(async () => {
		setLoadingEvents(true);
		await handleLoadData();
	}, [dateRange, resources]);

	useEffect(() => {
		setLoadingEvents(false);
	}, [events]);

	const handleSetResources = () => {
		if (!workplacesIds.length) {
			setWorkplacesIds([workplaceId]);
		}
		setResources(workplaces.filter(w => workplacesIds.indexOf(w.id) !== -1));
	};

	const handlePickDate = (date: Moment, resourceId?: string) => {
		setSelectedDate(date);
		setIsDetailOpened(true);
	};

	const handleEventClick = (event: EventClickArg) => {
		setSelectedEvent(event);
		setSelectedDate(moment(event.event.start));
		setIsDetailOpened(true);
	};

	const handleDateChange = (start: Moment, end: Moment) => {
		setDateRange({ start, end });
	};

	const handleClose = async () => {
		setSelectedEvent(undefined);
		setIsDetailOpened(false);
		await handleLoadData();
	};

	const handleLoadData = async () => {
		if (!dateRange?.start || !resources.length) {
			setEvents([]);
			return;
		}

		const a = async () => {
			const events = await getWorkersAbsenceEvents({
				dateFrom: getMomentDate(dateRange.start),
				dateTo: getMomentDate(dateRange.end),
				workplaceIds: resources.map((r: any) => r.id),
			});
			setEvents(events);
		};

		await a();
	};

	const handleEventDrop = async (event: EventApi) => {
		let workplaceId: string | undefined = undefined;
		for (const resource of event.getResources()) {
			workplaceId = resource.id;
		}

		const a = async () => {
			await apiClient.patch(`/event/${event.id}`, {
				dateFrom: moment(event.start).format(),
				dateTo: moment(event.end).format(),
				propIgnoreFreeTimeCheck: true,
				workplaceId,
			});
		};

		await a();
		await handleLoadData();
	};

	const handleLoadResources = async () => {
		const tempWorkplaces = [];

		const { data } = await apiClient.graphql({
			query: WORKPLACES,
			variables: {
				type: 'normal',
			},
			fetchPolicy: 'network-only',
		});

		for (const workplace of data.workplaces.data || []) {
			const item = {
				id: workplace.id,
				value: workplace.id,
				title: workplace.doctorDisplayName,
				label: workplace.doctorDisplayName,
				businessHours: [],
			};

			tempWorkplaces.push(item);

			if (workplace.id === workplaceId) {
				setDefaultValues({
					workplace: {
						value: workplace.id,
						label: workplace.doctorDisplayName,
					},
				});
			}
		}

		setWorkplaces(tempWorkplaces);
	};

	const handleSelectWorkplaces = async (values: Workplace) => {
		setWorkplacesIds([values.id]);
	};

	return (
		initialized && (
			<div className={styles.wrapper}>
				<div className={styles.selectWrapper}>
					<Form defaultValues={defaultValues}>
						<FormControl
							name='workplace'
							onChange={handleSelectWorkplaces}
							isRequired={true}>
							<Select
								options={workplaces}
								isSearchable={true}
								placeholder={t('base.chooseWorkplace')}
								portal
							/>
						</FormControl>
					</Form>
				</div>
				<div className={styles.calendarWrapper}>
					<Calendar
						events={events}
						onTimeClick={handlePickDate}
						onEventClick={handleEventClick}
						initialView={'timeGridWeek'}
						onDateChange={handleDateChange}
						editable={true}
						onEventDrop={handleEventDrop}
						onEventResize={handleEventDrop}
						resources={resources}
						loadingEvents={loadingEvents}
						renderEventItem={renderAbsenceEvent}
					/>
					{isDetailOpened && selectedDate && (
						<CalendarAbsenceEventModal
							isOpen
							onClose={handleClose}
							selectedDate={selectedDate}
							event={selectedEvent}
							workplaceId={workplaceId}
						/>
					)}
				</div>
			</div>
		)
	);
}
